import DOMPurify from 'dompurify';
import { isEmpty, find, get, upperFirst, startCase, isEqual, differenceWith } from 'lodash-es';

/*!
* tabbable 6.2.0
* @license MIT, https://github.com/focus-trap/tabbable/blob/master/LICENSE
*/
// NOTE: separate `:not()` selectors has broader browser support than the newer
//  `:not([inert], [inert] *)` (Feb 2023)
// CAREFUL: JSDom does not support `:not([inert] *)` as a selector; using it causes
//  the entire query to fail, resulting in no nodes found, which will break a lot
//  of things... so we have to rely on JS to identify nodes inside an inert container
var candidateSelectors = ['input:not([inert])', 'select:not([inert])', 'textarea:not([inert])', 'a[href]:not([inert])', 'button:not([inert])', '[tabindex]:not(slot):not([inert])', 'audio[controls]:not([inert])', 'video[controls]:not([inert])', '[contenteditable]:not([contenteditable="false"]):not([inert])', 'details>summary:first-of-type:not([inert])', 'details:not([inert])'];
var candidateSelector = /* #__PURE__ */candidateSelectors.join(',');
var NoElement = typeof Element === 'undefined';
var matches = NoElement ? function () {} : Element.prototype.matches || Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
var getRootNode = !NoElement && Element.prototype.getRootNode ? function (element) {
  var _element$getRootNode;
  return element === null || element === void 0 ? void 0 : (_element$getRootNode = element.getRootNode) === null || _element$getRootNode === void 0 ? void 0 : _element$getRootNode.call(element);
} : function (element) {
  return element === null || element === void 0 ? void 0 : element.ownerDocument;
};

/**
 * Determines if a node is inert or in an inert ancestor.
 * @param {Element} [node]
 * @param {boolean} [lookUp] If true and `node` is not inert, looks up at ancestors to
 *  see if any of them are inert. If false, only `node` itself is considered.
 * @returns {boolean} True if inert itself or by way of being in an inert ancestor.
 *  False if `node` is falsy.
 */
var isInert = function isInert(node, lookUp) {
  var _node$getAttribute;
  if (lookUp === void 0) {
    lookUp = true;
  }
  // CAREFUL: JSDom does not support inert at all, so we can't use the `HTMLElement.inert`
  //  JS API property; we have to check the attribute, which can either be empty or 'true';
  //  if it's `null` (not specified) or 'false', it's an active element
  var inertAtt = node === null || node === void 0 ? void 0 : (_node$getAttribute = node.getAttribute) === null || _node$getAttribute === void 0 ? void 0 : _node$getAttribute.call(node, 'inert');
  var inert = inertAtt === '' || inertAtt === 'true';

  // NOTE: this could also be handled with `node.matches('[inert], :is([inert] *)')`
  //  if it weren't for `matches()` not being a function on shadow roots; the following
  //  code works for any kind of node
  // CAREFUL: JSDom does not appear to support certain selectors like `:not([inert] *)`
  //  so it likely would not support `:is([inert] *)` either...
  var result = inert || lookUp && node && isInert(node.parentNode); // recursive

  return result;
};

/**
 * Determines if a node's content is editable.
 * @param {Element} [node]
 * @returns True if it's content-editable; false if it's not or `node` is falsy.
 */
var isContentEditable = function isContentEditable(node) {
  var _node$getAttribute2;
  // CAREFUL: JSDom does not support the `HTMLElement.isContentEditable` API so we have
  //  to use the attribute directly to check for this, which can either be empty or 'true';
  //  if it's `null` (not specified) or 'false', it's a non-editable element
  var attValue = node === null || node === void 0 ? void 0 : (_node$getAttribute2 = node.getAttribute) === null || _node$getAttribute2 === void 0 ? void 0 : _node$getAttribute2.call(node, 'contenteditable');
  return attValue === '' || attValue === 'true';
};

/**
 * @param {Element} el container to check in
 * @param {boolean} includeContainer add container to check
 * @param {(node: Element) => boolean} filter filter candidates
 * @returns {Element[]}
 */
var getCandidates = function getCandidates(el, includeContainer, filter) {
  // even if `includeContainer=false`, we still have to check it for inertness because
  //  if it's inert, all its children are inert
  if (isInert(el)) {
    return [];
  }
  var candidates = Array.prototype.slice.apply(el.querySelectorAll(candidateSelector));
  if (includeContainer && matches.call(el, candidateSelector)) {
    candidates.unshift(el);
  }
  candidates = candidates.filter(filter);
  return candidates;
};

/**
 * @callback GetShadowRoot
 * @param {Element} element to check for shadow root
 * @returns {ShadowRoot|boolean} ShadowRoot if available or boolean indicating if a shadowRoot is attached but not available.
 */

/**
 * @callback ShadowRootFilter
 * @param {Element} shadowHostNode the element which contains shadow content
 * @returns {boolean} true if a shadow root could potentially contain valid candidates.
 */

/**
 * @typedef {Object} CandidateScope
 * @property {Element} scopeParent contains inner candidates
 * @property {Element[]} candidates list of candidates found in the scope parent
 */

/**
 * @typedef {Object} IterativeOptions
 * @property {GetShadowRoot|boolean} getShadowRoot true if shadow support is enabled; falsy if not;
 *  if a function, implies shadow support is enabled and either returns the shadow root of an element
 *  or a boolean stating if it has an undisclosed shadow root
 * @property {(node: Element) => boolean} filter filter candidates
 * @property {boolean} flatten if true then result will flatten any CandidateScope into the returned list
 * @property {ShadowRootFilter} shadowRootFilter filter shadow roots;
 */

/**
 * @param {Element[]} elements list of element containers to match candidates from
 * @param {boolean} includeContainer add container list to check
 * @param {IterativeOptions} options
 * @returns {Array.<Element|CandidateScope>}
 */
var getCandidatesIteratively = function getCandidatesIteratively(elements, includeContainer, options) {
  var candidates = [];
  var elementsToCheck = Array.from(elements);
  while (elementsToCheck.length) {
    var element = elementsToCheck.shift();
    if (isInert(element, false)) {
      // no need to look up since we're drilling down
      // anything inside this container will also be inert
      continue;
    }
    if (element.tagName === 'SLOT') {
      // add shadow dom slot scope (slot itself cannot be focusable)
      var assigned = element.assignedElements();
      var content = assigned.length ? assigned : element.children;
      var nestedCandidates = getCandidatesIteratively(content, true, options);
      if (options.flatten) {
        candidates.push.apply(candidates, nestedCandidates);
      } else {
        candidates.push({
          scopeParent: element,
          candidates: nestedCandidates
        });
      }
    } else {
      // check candidate element
      var validCandidate = matches.call(element, candidateSelector);
      if (validCandidate && options.filter(element) && (includeContainer || !elements.includes(element))) {
        candidates.push(element);
      }

      // iterate over shadow content if possible
      var shadowRoot = element.shadowRoot ||
      // check for an undisclosed shadow
      typeof options.getShadowRoot === 'function' && options.getShadowRoot(element);

      // no inert look up because we're already drilling down and checking for inertness
      //  on the way down, so all containers to this root node should have already been
      //  vetted as non-inert
      var validShadowRoot = !isInert(shadowRoot, false) && (!options.shadowRootFilter || options.shadowRootFilter(element));
      if (shadowRoot && validShadowRoot) {
        // add shadow dom scope IIF a shadow root node was given; otherwise, an undisclosed
        //  shadow exists, so look at light dom children as fallback BUT create a scope for any
        //  child candidates found because they're likely slotted elements (elements that are
        //  children of the web component element (which has the shadow), in the light dom, but
        //  slotted somewhere _inside_ the undisclosed shadow) -- the scope is created below,
        //  _after_ we return from this recursive call
        var _nestedCandidates = getCandidatesIteratively(shadowRoot === true ? element.children : shadowRoot.children, true, options);
        if (options.flatten) {
          candidates.push.apply(candidates, _nestedCandidates);
        } else {
          candidates.push({
            scopeParent: element,
            candidates: _nestedCandidates
          });
        }
      } else {
        // there's not shadow so just dig into the element's (light dom) children
        //  __without__ giving the element special scope treatment
        elementsToCheck.unshift.apply(elementsToCheck, element.children);
      }
    }
  }
  return candidates;
};

/**
 * @private
 * Determines if the node has an explicitly specified `tabindex` attribute.
 * @param {HTMLElement} node
 * @returns {boolean} True if so; false if not.
 */
var hasTabIndex = function hasTabIndex(node) {
  return !isNaN(parseInt(node.getAttribute('tabindex'), 10));
};

/**
 * Determine the tab index of a given node.
 * @param {HTMLElement} node
 * @returns {number} Tab order (negative, 0, or positive number).
 * @throws {Error} If `node` is falsy.
 */
var getTabIndex = function getTabIndex(node) {
  if (!node) {
    throw new Error('No node provided');
  }
  if (node.tabIndex < 0) {
    // in Chrome, <details/>, <audio controls/> and <video controls/> elements get a default
    // `tabIndex` of -1 when the 'tabindex' attribute isn't specified in the DOM,
    // yet they are still part of the regular tab order; in FF, they get a default
    // `tabIndex` of 0; since Chrome still puts those elements in the regular tab
    // order, consider their tab index to be 0.
    // Also browsers do not return `tabIndex` correctly for contentEditable nodes;
    // so if they don't have a tabindex attribute specifically set, assume it's 0.
    if ((/^(AUDIO|VIDEO|DETAILS)$/.test(node.tagName) || isContentEditable(node)) && !hasTabIndex(node)) {
      return 0;
    }
  }
  return node.tabIndex;
};

/**
 * Determine the tab index of a given node __for sort order purposes__.
 * @param {HTMLElement} node
 * @param {boolean} [isScope] True for a custom element with shadow root or slot that, by default,
 *  has tabIndex -1, but needs to be sorted by document order in order for its content to be
 *  inserted into the correct sort position.
 * @returns {number} Tab order (negative, 0, or positive number).
 */
var getSortOrderTabIndex = function getSortOrderTabIndex(node, isScope) {
  var tabIndex = getTabIndex(node);
  if (tabIndex < 0 && isScope && !hasTabIndex(node)) {
    return 0;
  }
  return tabIndex;
};
var sortOrderedTabbables = function sortOrderedTabbables(a, b) {
  return a.tabIndex === b.tabIndex ? a.documentOrder - b.documentOrder : a.tabIndex - b.tabIndex;
};
var isInput = function isInput(node) {
  return node.tagName === 'INPUT';
};
var isHiddenInput = function isHiddenInput(node) {
  return isInput(node) && node.type === 'hidden';
};
var isDetailsWithSummary = function isDetailsWithSummary(node) {
  var r = node.tagName === 'DETAILS' && Array.prototype.slice.apply(node.children).some(function (child) {
    return child.tagName === 'SUMMARY';
  });
  return r;
};
var getCheckedRadio = function getCheckedRadio(nodes, form) {
  for (var i = 0; i < nodes.length; i++) {
    if (nodes[i].checked && nodes[i].form === form) {
      return nodes[i];
    }
  }
};
var isTabbableRadio = function isTabbableRadio(node) {
  if (!node.name) {
    return true;
  }
  var radioScope = node.form || getRootNode(node);
  var queryRadios = function queryRadios(name) {
    return radioScope.querySelectorAll('input[type="radio"][name="' + name + '"]');
  };
  var radioSet;
  if (typeof window !== 'undefined' && typeof window.CSS !== 'undefined' && typeof window.CSS.escape === 'function') {
    radioSet = queryRadios(window.CSS.escape(node.name));
  } else {
    try {
      radioSet = queryRadios(node.name);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Looks like you have a radio button with a name attribute containing invalid CSS selector characters and need the CSS.escape polyfill: %s', err.message);
      return false;
    }
  }
  var checked = getCheckedRadio(radioSet, node.form);
  return !checked || checked === node;
};
var isRadio = function isRadio(node) {
  return isInput(node) && node.type === 'radio';
};
var isNonTabbableRadio = function isNonTabbableRadio(node) {
  return isRadio(node) && !isTabbableRadio(node);
};

// determines if a node is ultimately attached to the window's document
var isNodeAttached = function isNodeAttached(node) {
  var _nodeRoot;
  // The root node is the shadow root if the node is in a shadow DOM; some document otherwise
  //  (but NOT _the_ document; see second 'If' comment below for more).
  // If rootNode is shadow root, it'll have a host, which is the element to which the shadow
  //  is attached, and the one we need to check if it's in the document or not (because the
  //  shadow, and all nodes it contains, is never considered in the document since shadows
  //  behave like self-contained DOMs; but if the shadow's HOST, which is part of the document,
  //  is hidden, or is not in the document itself but is detached, it will affect the shadow's
  //  visibility, including all the nodes it contains). The host could be any normal node,
  //  or a custom element (i.e. web component). Either way, that's the one that is considered
  //  part of the document, not the shadow root, nor any of its children (i.e. the node being
  //  tested).
  // To further complicate things, we have to look all the way up until we find a shadow HOST
  //  that is attached (or find none) because the node might be in nested shadows...
  // If rootNode is not a shadow root, it won't have a host, and so rootNode should be the
  //  document (per the docs) and while it's a Document-type object, that document does not
  //  appear to be the same as the node's `ownerDocument` for some reason, so it's safer
  //  to ignore the rootNode at this point, and use `node.ownerDocument`. Otherwise,
  //  using `rootNode.contains(node)` will _always_ be true we'll get false-positives when
  //  node is actually detached.
  // NOTE: If `nodeRootHost` or `node` happens to be the `document` itself (which is possible
  //  if a tabbable/focusable node was quickly added to the DOM, focused, and then removed
  //  from the DOM as in https://github.com/focus-trap/focus-trap-react/issues/905), then
  //  `ownerDocument` will be `null`, hence the optional chaining on it.
  var nodeRoot = node && getRootNode(node);
  var nodeRootHost = (_nodeRoot = nodeRoot) === null || _nodeRoot === void 0 ? void 0 : _nodeRoot.host;

  // in some cases, a detached node will return itself as the root instead of a document or
  //  shadow root object, in which case, we shouldn't try to look further up the host chain
  var attached = false;
  if (nodeRoot && nodeRoot !== node) {
    var _nodeRootHost, _nodeRootHost$ownerDo, _node$ownerDocument;
    attached = !!((_nodeRootHost = nodeRootHost) !== null && _nodeRootHost !== void 0 && (_nodeRootHost$ownerDo = _nodeRootHost.ownerDocument) !== null && _nodeRootHost$ownerDo !== void 0 && _nodeRootHost$ownerDo.contains(nodeRootHost) || node !== null && node !== void 0 && (_node$ownerDocument = node.ownerDocument) !== null && _node$ownerDocument !== void 0 && _node$ownerDocument.contains(node));
    while (!attached && nodeRootHost) {
      var _nodeRoot2, _nodeRootHost2, _nodeRootHost2$ownerD;
      // since it's not attached and we have a root host, the node MUST be in a nested shadow DOM,
      //  which means we need to get the host's host and check if that parent host is contained
      //  in (i.e. attached to) the document
      nodeRoot = getRootNode(nodeRootHost);
      nodeRootHost = (_nodeRoot2 = nodeRoot) === null || _nodeRoot2 === void 0 ? void 0 : _nodeRoot2.host;
      attached = !!((_nodeRootHost2 = nodeRootHost) !== null && _nodeRootHost2 !== void 0 && (_nodeRootHost2$ownerD = _nodeRootHost2.ownerDocument) !== null && _nodeRootHost2$ownerD !== void 0 && _nodeRootHost2$ownerD.contains(nodeRootHost));
    }
  }
  return attached;
};
var isZeroArea = function isZeroArea(node) {
  var _node$getBoundingClie = node.getBoundingClientRect(),
    width = _node$getBoundingClie.width,
    height = _node$getBoundingClie.height;
  return width === 0 && height === 0;
};
var isHidden = function isHidden(node, _ref) {
  var displayCheck = _ref.displayCheck,
    getShadowRoot = _ref.getShadowRoot;
  // NOTE: visibility will be `undefined` if node is detached from the document
  //  (see notes about this further down), which means we will consider it visible
  //  (this is legacy behavior from a very long way back)
  // NOTE: we check this regardless of `displayCheck="none"` because this is a
  //  _visibility_ check, not a _display_ check
  if (getComputedStyle(node).visibility === 'hidden') {
    return true;
  }
  var isDirectSummary = matches.call(node, 'details>summary:first-of-type');
  var nodeUnderDetails = isDirectSummary ? node.parentElement : node;
  if (matches.call(nodeUnderDetails, 'details:not([open]) *')) {
    return true;
  }
  if (!displayCheck || displayCheck === 'full' || displayCheck === 'legacy-full') {
    if (typeof getShadowRoot === 'function') {
      // figure out if we should consider the node to be in an undisclosed shadow and use the
      //  'non-zero-area' fallback
      var originalNode = node;
      while (node) {
        var parentElement = node.parentElement;
        var rootNode = getRootNode(node);
        if (parentElement && !parentElement.shadowRoot && getShadowRoot(parentElement) === true // check if there's an undisclosed shadow
        ) {
          // node has an undisclosed shadow which means we can only treat it as a black box, so we
          //  fall back to a non-zero-area test
          return isZeroArea(node);
        } else if (node.assignedSlot) {
          // iterate up slot
          node = node.assignedSlot;
        } else if (!parentElement && rootNode !== node.ownerDocument) {
          // cross shadow boundary
          node = rootNode.host;
        } else {
          // iterate up normal dom
          node = parentElement;
        }
      }
      node = originalNode;
    }
    // else, `getShadowRoot` might be true, but all that does is enable shadow DOM support
    //  (i.e. it does not also presume that all nodes might have undisclosed shadows); or
    //  it might be a falsy value, which means shadow DOM support is disabled

    // Since we didn't find it sitting in an undisclosed shadow (or shadows are disabled)
    //  now we can just test to see if it would normally be visible or not, provided it's
    //  attached to the main document.
    // NOTE: We must consider case where node is inside a shadow DOM and given directly to
    //  `isTabbable()` or `isFocusable()` -- regardless of `getShadowRoot` option setting.

    if (isNodeAttached(node)) {
      // this works wherever the node is: if there's at least one client rect, it's
      //  somehow displayed; it also covers the CSS 'display: contents' case where the
      //  node itself is hidden in place of its contents; and there's no need to search
      //  up the hierarchy either
      return !node.getClientRects().length;
    }

    // Else, the node isn't attached to the document, which means the `getClientRects()`
    //  API will __always__ return zero rects (this can happen, for example, if React
    //  is used to render nodes onto a detached tree, as confirmed in this thread:
    //  https://github.com/facebook/react/issues/9117#issuecomment-284228870)
    //
    // It also means that even window.getComputedStyle(node).display will return `undefined`
    //  because styles are only computed for nodes that are in the document.
    //
    // NOTE: THIS HAS BEEN THE CASE FOR YEARS. It is not new, nor is it caused by tabbable
    //  somehow. Though it was never stated officially, anyone who has ever used tabbable
    //  APIs on nodes in detached containers has actually implicitly used tabbable in what
    //  was later (as of v5.2.0 on Apr 9, 2021) called `displayCheck="none"` mode -- essentially
    //  considering __everything__ to be visible because of the innability to determine styles.
    //
    // v6.0.0: As of this major release, the default 'full' option __no longer treats detached
    //  nodes as visible with the 'none' fallback.__
    if (displayCheck !== 'legacy-full') {
      return true; // hidden
    }
    // else, fallback to 'none' mode and consider the node visible
  } else if (displayCheck === 'non-zero-area') {
    // NOTE: Even though this tests that the node's client rect is non-zero to determine
    //  whether it's displayed, and that a detached node will __always__ have a zero-area
    //  client rect, we don't special-case for whether the node is attached or not. In
    //  this mode, we do want to consider nodes that have a zero area to be hidden at all
    //  times, and that includes attached or not.
    return isZeroArea(node);
  }

  // visible, as far as we can tell, or per current `displayCheck=none` mode, we assume
  //  it's visible
  return false;
};

// form fields (nested) inside a disabled fieldset are not focusable/tabbable
//  unless they are in the _first_ <legend> element of the top-most disabled
//  fieldset
var isDisabledFromFieldset = function isDisabledFromFieldset(node) {
  if (/^(INPUT|BUTTON|SELECT|TEXTAREA)$/.test(node.tagName)) {
    var parentNode = node.parentElement;
    // check if `node` is contained in a disabled <fieldset>
    while (parentNode) {
      if (parentNode.tagName === 'FIELDSET' && parentNode.disabled) {
        // look for the first <legend> among the children of the disabled <fieldset>
        for (var i = 0; i < parentNode.children.length; i++) {
          var child = parentNode.children.item(i);
          // when the first <legend> (in document order) is found
          if (child.tagName === 'LEGEND') {
            // if its parent <fieldset> is not nested in another disabled <fieldset>,
            // return whether `node` is a descendant of its first <legend>
            return matches.call(parentNode, 'fieldset[disabled] *') ? true : !child.contains(node);
          }
        }
        // the disabled <fieldset> containing `node` has no <legend>
        return true;
      }
      parentNode = parentNode.parentElement;
    }
  }

  // else, node's tabbable/focusable state should not be affected by a fieldset's
  //  enabled/disabled state
  return false;
};
var isNodeMatchingSelectorFocusable = function isNodeMatchingSelectorFocusable(options, node) {
  if (node.disabled ||
  // we must do an inert look up to filter out any elements inside an inert ancestor
  //  because we're limited in the type of selectors we can use in JSDom (see related
  //  note related to `candidateSelectors`)
  isInert(node) || isHiddenInput(node) || isHidden(node, options) ||
  // For a details element with a summary, the summary element gets the focus
  isDetailsWithSummary(node) || isDisabledFromFieldset(node)) {
    return false;
  }
  return true;
};
var isNodeMatchingSelectorTabbable = function isNodeMatchingSelectorTabbable(options, node) {
  if (isNonTabbableRadio(node) || getTabIndex(node) < 0 || !isNodeMatchingSelectorFocusable(options, node)) {
    return false;
  }
  return true;
};
var isValidShadowRootTabbable = function isValidShadowRootTabbable(shadowHostNode) {
  var tabIndex = parseInt(shadowHostNode.getAttribute('tabindex'), 10);
  if (isNaN(tabIndex) || tabIndex >= 0) {
    return true;
  }
  // If a custom element has an explicit negative tabindex,
  // browsers will not allow tab targeting said element's children.
  return false;
};

/**
 * @param {Array.<Element|CandidateScope>} candidates
 * @returns Element[]
 */
var sortByOrder = function sortByOrder(candidates) {
  var regularTabbables = [];
  var orderedTabbables = [];
  candidates.forEach(function (item, i) {
    var isScope = !!item.scopeParent;
    var element = isScope ? item.scopeParent : item;
    var candidateTabindex = getSortOrderTabIndex(element, isScope);
    var elements = isScope ? sortByOrder(item.candidates) : element;
    if (candidateTabindex === 0) {
      isScope ? regularTabbables.push.apply(regularTabbables, elements) : regularTabbables.push(element);
    } else {
      orderedTabbables.push({
        documentOrder: i,
        tabIndex: candidateTabindex,
        item: item,
        isScope: isScope,
        content: elements
      });
    }
  });
  return orderedTabbables.sort(sortOrderedTabbables).reduce(function (acc, sortable) {
    sortable.isScope ? acc.push.apply(acc, sortable.content) : acc.push(sortable.content);
    return acc;
  }, []).concat(regularTabbables);
};
var tabbable = function tabbable(container, options) {
  options = options || {};
  var candidates;
  if (options.getShadowRoot) {
    candidates = getCandidatesIteratively([container], options.includeContainer, {
      filter: isNodeMatchingSelectorTabbable.bind(null, options),
      flatten: false,
      getShadowRoot: options.getShadowRoot,
      shadowRootFilter: isValidShadowRootTabbable
    });
  } else {
    candidates = getCandidates(container, options.includeContainer, isNodeMatchingSelectorTabbable.bind(null, options));
  }
  return sortByOrder(candidates);
};
var isTabbable = function isTabbable(node, options) {
  options = options || {};
  if (!node) {
    throw new Error('No node provided');
  }
  if (matches.call(node, candidateSelector) === false) {
    return false;
  }
  return isNodeMatchingSelectorTabbable(options, node);
};

function eventPath(evt) {
    const composedPath = evt.composedPath && evt.composedPath();
    const path = composedPath && composedPath.length ? composedPath : evt.path;
    const { target } = evt;
    if (path != null) {
        // Safari doesn't include Window, but it should.
        return path.indexOf(window) < 0 ? path.concat(window) : path;
    }
    if (target === window) {
        return [window];
    }
    function getParents(node, memo = []) {
        const { parentNode } = node;
        if (!parentNode) {
            return memo;
        }
        return getParents(parentNode, memo.concat(parentNode));
    }
    return [target].concat(getParents(target), window);
}

const BXF_INLINE_MENU_TAGNAME = 'BXF-INLINE-MENU';
const BXF_ASSISTIVE_TEXT_HIDDEN = 'bxf__assistive-text--hidden';
const BXF_MOUSE_FOCUS = 'bxf__mouse-focus';
const BXF_FOCUS_KEYBOARD_ONLY = 'bxf__focus--keyboard-only';
const HOVER_IN_DELAY = 150;
const CANVAS_ID = 'bxf-get-text-width-canvas';
const DEFAULT_Z_INDEX = '1100';
const DEFAULT_FONT = `13px 'BLK Fort', sans-serif`;
// EBC apps related
const WINDOW_CONTROLS_OVERLAY_ENABLED = '--window-controls-overlay-enabled';
const WINDOW_CONTROLS_OVERLAY_HEIGHT = '--window-controls-overlay-height';
const CHAT_COMPONENT_CONSOLE_WARNING = 'Chat components are for use within Aladdin Copilot. Please email the +Group Aladdin Design System Operations distribution list (GroupAladdinDesignSystemOperations@blackrock.com) to request access.';
const COPILOT_CANVAS_WRAPPER = 'chat-canvas-wrapper';
const AXF_HEADER_CONTAINER = 'axf-header-container';
const GRID_POPUP = 'ag-popup';
const GRID_FLOATING_FILTER_INPUT = 'ag-floating-filter-input';
const GRID_FLOATING_FILTER_BUTTON = 'ag-floating-filter-button';

var AriaAttr;
(function (AriaAttr) {
    AriaAttr["AUTOCOMPLETE"] = "aria-autocomplete";
    AriaAttr["ATOMIC"] = "aria-atomic";
    AriaAttr["CHECKED"] = "aria-checked";
    AriaAttr["DISABLED"] = "aria-disabled";
    AriaAttr["DESCRIBEDBY"] = "aria-describedby";
    AriaAttr["EXPANDED"] = "aria-expanded";
    AriaAttr["HASPOPUP"] = "aria-haspopup";
    AriaAttr["HIDDEN"] = "aria-hidden";
    AriaAttr["INVALID"] = "aria-invalid";
    AriaAttr["LABEL"] = "aria-label";
    AriaAttr["LABELLEDBY"] = "aria-labelledby";
    AriaAttr["LEVEL"] = "aria-level";
    AriaAttr["LIVE"] = "aria-live";
    AriaAttr["MULTISELECTABLE"] = "aria-multiselectable";
    AriaAttr["OWNS"] = "aria-owns";
    AriaAttr["POSINSET"] = "aria-posinset";
    AriaAttr["REQUIRED"] = "aria-required";
    AriaAttr["ROLE"] = "role";
    AriaAttr["ROLEDESCRIPTION"] = "aria-roledescription";
    AriaAttr["SELECTED"] = "aria-selected";
    AriaAttr["SETSIZE"] = "aria-setsize";
    AriaAttr["VALUENOW"] = "aria-valuenow";
    AriaAttr["VALUEMAX"] = "aria-valuemax";
    AriaAttr["VALUEMIN"] = "aria-valuemin";
    AriaAttr["VALUETEXT"] = "aria-valuetext";
})(AriaAttr || (AriaAttr = {}));

var WindowEventMap;
(function (WindowEventMap) {
    WindowEventMap["abort"] = "abort";
    WindowEventMap["afterprint"] = "afterprint";
    WindowEventMap["beforeprint"] = "beforeprint";
    WindowEventMap["beforeunload"] = "beforeunload";
    WindowEventMap["blur"] = "blur";
    WindowEventMap["canplay"] = "canplay";
    WindowEventMap["canplaythrough"] = "canplaythrough";
    WindowEventMap["change"] = "change";
    WindowEventMap["click"] = "click";
    WindowEventMap["compassneedscalibration"] = "compassneedscalibration";
    WindowEventMap["contextmenu"] = "contextmenu";
    WindowEventMap["dblclick"] = "dblclick";
    WindowEventMap["devicelight"] = "devicelight";
    WindowEventMap["devicemotion"] = "devicemotion";
    WindowEventMap["deviceorientation"] = "deviceorientation";
    WindowEventMap["deviceorientationabsolute"] = "deviceorientationabsolute";
    WindowEventMap["drag"] = "drag";
    WindowEventMap["dragend"] = "dragend";
    WindowEventMap["dragenter"] = "dragenter";
    WindowEventMap["dragleave"] = "dragleave";
    WindowEventMap["dragover"] = "dragover";
    WindowEventMap["dragstart"] = "dragstart";
    WindowEventMap["drop"] = "drop";
    WindowEventMap["durationchange"] = "durationchange";
    WindowEventMap["emptied"] = "emptied";
    WindowEventMap["ended"] = "ended";
    WindowEventMap["error"] = "error";
    WindowEventMap["focus"] = "focus";
    WindowEventMap["focusin"] = "focusin";
    WindowEventMap["focusout"] = "focusout";
    WindowEventMap["focusChanged"] = "focusChanged";
    WindowEventMap["hashchange"] = "hashchange";
    WindowEventMap["input"] = "input";
    WindowEventMap["invalid"] = "invalid";
    WindowEventMap["keydown"] = "keydown";
    WindowEventMap["keypress"] = "keypress";
    WindowEventMap["keyup"] = "keyup";
    WindowEventMap["load"] = "load";
    WindowEventMap["loadeddata"] = "loadeddata";
    WindowEventMap["loadedmetadata"] = "loadedmetadata";
    WindowEventMap["loadstart"] = "loadstart";
    WindowEventMap["message"] = "message";
    WindowEventMap["mousedown"] = "mousedown";
    WindowEventMap["mouseenter"] = "mouseenter";
    WindowEventMap["mouseleave"] = "mouseleave";
    WindowEventMap["mousemove"] = "mousemove";
    WindowEventMap["mouseout"] = "mouseout";
    WindowEventMap["mouseover"] = "mouseover";
    WindowEventMap["mouseup"] = "mouseup";
    WindowEventMap["mousewheel"] = "mousewheel";
    WindowEventMap["MSGestureChange"] = "MSGestureChange";
    WindowEventMap["MSGestureDoubleTap"] = "MSGestureDoubleTap";
    WindowEventMap["MSGestureEnd"] = "MSGestureEnd";
    WindowEventMap["MSGestureHold"] = "MSGestureHold";
    WindowEventMap["MSGestureStart"] = "MSGestureStart";
    WindowEventMap["MSGestureTap"] = "MSGestureTap";
    WindowEventMap["MSInertiaStart"] = "MSInertiaStart";
    WindowEventMap["MSPointerCancel"] = "MSPointerCancel";
    WindowEventMap["MSPointerDown"] = "MSPointerDown";
    WindowEventMap["MSPointerEnter"] = "MSPointerEnter";
    WindowEventMap["MSPointerLeave"] = "MSPointerLeave";
    WindowEventMap["MSPointerMove"] = "MSPointerMove";
    WindowEventMap["MSPointerOut"] = "MSPointerOut";
    WindowEventMap["MSPointerOver"] = "MSPointerOver";
    WindowEventMap["MSPointerUp"] = "MSPointerUp";
    WindowEventMap["offline"] = "offline";
    WindowEventMap["online"] = "online";
    WindowEventMap["orientationchange"] = "orientationchange";
    WindowEventMap["pagehide"] = "pagehide";
    WindowEventMap["pageshow"] = "pageshow";
    WindowEventMap["pause"] = "pause";
    WindowEventMap["play"] = "play";
    WindowEventMap["playing"] = "playing";
    WindowEventMap["popstate"] = "popstate";
    WindowEventMap["progress"] = "progress";
    WindowEventMap["ratechange"] = "ratechange";
    WindowEventMap["readystatechange"] = "readystatechange";
    WindowEventMap["reset"] = "reset";
    WindowEventMap["resize"] = "resize";
    WindowEventMap["scroll"] = "scroll";
    WindowEventMap["seeked"] = "seeked";
    WindowEventMap["seeking"] = "seeking";
    WindowEventMap["select"] = "select";
    WindowEventMap["stalled"] = "stalled";
    WindowEventMap["storage"] = "storage";
    WindowEventMap["submit"] = "submit";
    WindowEventMap["suspend"] = "suspend";
    WindowEventMap["touchstart"] = "touchstart";
    WindowEventMap["timeupdate"] = "timeupdate";
    WindowEventMap["unload"] = "unload";
    WindowEventMap["volumechange"] = "volumechange";
    WindowEventMap["vrdisplayactivate"] = "vrdisplayactivate";
    WindowEventMap["vrdisplayblur"] = "vrdisplayblur";
    WindowEventMap["vrdisplayconnect"] = "vrdisplayconnect";
    WindowEventMap["vrdisplaydeactivate"] = "vrdisplaydeactivate";
    WindowEventMap["vrdisplaydisconnect"] = "vrdisplaydisconnect";
    WindowEventMap["vrdisplayfocus"] = "vrdisplayfocus";
    WindowEventMap["vrdisplaypointerrestricted"] = "vrdisplaypointerrestricted";
    WindowEventMap["vrdisplaypointerunrestricted"] = "vrdisplaypointerunrestricted";
    WindowEventMap["vrdisplaypresentchange"] = "vrdisplaypresentchange";
    WindowEventMap["waiting"] = "waiting";
    WindowEventMap["contextMenuItemClicked"] = "contextMenuItemClicked";
})(WindowEventMap || (WindowEventMap = {}));

function addEventListenerOnElement(element, eventName, callback) {
    if (element && eventName && callback) {
        element.addEventListener(eventName, callback);
    }
}
function removeEventListenerOnElement(element, eventName, callback) {
    if (element && eventName && callback) {
        element.removeEventListener(eventName, callback);
    }
}
/**
 * Function to check if a slot exists in the host or shadowRoot
 * @param {HTMLElement} host The host element to check for slot
 * @param {string} slotName The slot name to check for
 * @returns {boolean}
 */
function hasSlot(host, slotName) {
    const slot = `[slot="${slotName}"]`;
    return (host && !isEmpty(host.querySelector(slot) || host?.querySelector(slot)));
}
/**
 * Formats a given timestamp into a human-readable relative time string.
 *
 * @param createTime - The timestamp string to format, in a format parsable by the `Date` constructor.
 * @returns A string representing the relative time since the given timestamp.
 *          - "Just Now" if the time difference is less than a minute.
 *          - "{n} minute(s) ago" if the time difference is less than an hour.
 *          - "{n} hour(s) ago" if the time difference is less than a day.
 *          - "Yesterday" or "{n} days ago" if the time difference is less than a week.
 *          - A formatted date string (e.g., "Jan 1, 2023") if the time difference is a week or more.
 */
function formatTimestamp(createTime) {
    const now = new Date();
    const past = new Date(createTime);
    const deltaSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);
    const secondsInMinute = 60;
    const secondsInHour = 3600;
    const secondsInDay = 86400;
    const secondsInWeek = 604800;
    if (deltaSeconds < secondsInMinute) {
        return 'Just Now';
    }
    else if (deltaSeconds < secondsInHour) {
        const minutes = Math.floor(deltaSeconds / secondsInMinute);
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }
    else if (deltaSeconds < secondsInDay) {
        const hours = Math.floor(deltaSeconds / secondsInHour);
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    else if (deltaSeconds < secondsInWeek) {
        const days = Math.floor(deltaSeconds / secondsInDay);
        return days === 1 ? 'Yesterday' : `${days} days ago`;
    }
    else {
        return past.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }
}
/**
 * Gets all tabbable elements within a given HTML root element.
 * @param root - The root HTML element to search for tabbable elements.
 * @returns An array of HTMLElement objects that are tabbable.
 */
const getTabbableElements = (root) => {
    const tabbableEls = [];
    const traverse = (node) => {
        if (isTabbable(node)) {
            tabbableEls.push(node);
        }
        if (node.shadowRoot) {
            traverse(node.shadowRoot.firstElementChild);
        }
        else {
            Array.from(node.children)?.forEach((child) => {
                if (child?.nodeType === Node.ELEMENT_NODE) {
                    traverse(child);
                }
            });
        }
    };
    traverse(root);
    return tabbableEls;
};
function createElementWithClass(className, elementTag = 'div') {
    const element = document.createElement(elementTag);
    element.className = className;
    return element;
}
function elementExists(elementClassName) {
    return !isEmpty(document.getElementsByClassName(elementClassName));
}
function getElement(elementClassName) {
    return document.getElementsByClassName(elementClassName)[0];
}
/**
 * Given a keyboard event, return whether or not a grid popup is
 * 	in the event's path.
 * Used in overlays to prevent ag-grid popups from closing modal
 */
function isGridPopupInEscPath(event) {
    if (event) {
        const path = eventPath(event);
        if (path) {
            const agPopupElm = find(path, (el) => el &&
                el.classList &&
                (el.classList.contains(GRID_POPUP) ||
                    el.classList.contains(GRID_FLOATING_FILTER_BUTTON) ||
                    el.classList.contains(GRID_FLOATING_FILTER_INPUT)));
            return !!agPopupElm;
        }
    }
    return false;
}
function removeFromContainer(element, container) {
    if (element !== container && element !== document.body) {
        const parent = get(element, 'parentElement');
        if (parent) {
            parent.removeChild(element);
            if (get(parent, 'childElementCount') === 0) {
                removeFromContainer(parent, container);
            }
        }
    }
}
/**
 * Function to safely get shadowRoot node for a given host element.
 * @param {HTMLElement} root The host element for which shadowRoot is retrieved
 * @returns {ShadowRoot | HTMLElement}
 */
function getShadowRootElement(root) {
    // With polyfills for CustomElements and ShadowDOM, we can safely perform Element.shadowRoot. Stencil's compiler internally uses this polyfills to return the host element for Element.shadowRoot.
    return root?.shadowRoot;
}
/**
 * Function to safely get slotted elements for a given element
 * @param {ShadowRoot | HTMLElement} root The root element for which slotted elements are retrieved
 * @returns {Array<HTMLElement | Node>}
 */
function getSlottedElements(root) {
    if (root) {
        const slotElement = root.querySelector('slot');
        if (slotElement?.assignedNodes) {
            return slotElement.assignedNodes();
        }
    }
    return [];
}
/**
 * Force SVGs to change color in high contrast mode
 * @param svg svg element
 * @param hasStroke whether to use stroke
 * @param removeFill whether to remove fill
 */
function setSVGHiContrastOverrides(svg, hasStroke, removeFill, setFill = true) {
    if (svg) {
        svg.style['forced-color-adjust'] = 'auto';
        /**
         * Need to set fill: currentColor here,
         * 	as stenciljs scoped styling did not play nice with svg css selector
         */
        if (setFill) {
            svg.style.fill = 'currentColor';
        }
        if (hasStroke) {
            svg.style.stroke = 'currentColor';
            if (removeFill) {
                svg.style.fill = 'none';
            }
        }
        /**
         * Need to check for subtle icons to
         * fill in a different color schema than currentColor.
         */
        const iconState = svg.parentElement?.attributes['data-bxf-state']?.value;
        const subtleSelector = iconState?.includes('subtle');
        /**
         * Need to query for <circle> elements for border to not turn
         * 	white in dark mode
         */
        if (subtleSelector) {
            const circleElem = svg.querySelector('circle');
            if (circleElem) {
                circleElem.parentElement.removeChild(circleElem);
            }
        }
    }
}
/**
 * Creates selector
 * @param {string} selectorMain
 * @param {string | number} selectorSuffix
 * @param {boolean} swap
 * @returns {string}
 */
function createSelector(selectorMain, selectorSuffix, swap = false) {
    let selector;
    if (swap) {
        selector = selectorSuffix
            ? `${selectorSuffix.toString().replace(' ', '-')}-${selectorMain}`
            : selectorMain;
    }
    else {
        selector = selectorSuffix
            ? `${selectorMain}-${selectorSuffix.toString().replace(' ', '-')}`
            : selectorMain;
    }
    return selector.toLowerCase();
}
/**
 * Sanitize and encode all HTML in a user-submitted string
 * @param  {String} str  The user-submitted string
 * @return {String} str  The sanitized string
 */
function sanitizeHTML(str, options = { ALLOWED_TAGS: [] }) {
    if (isEmpty(str))
        return '';
    return DOMPurify.sanitize(str, options);
}
/**
 * Sanitization method that works with both HTML and SVG.
 */
function sanitizeSvg(str) {
    return DOMPurify.sanitize(str);
}
/**
 * Given an event, get the source button of the event
 * @param {Event} event
 */
function getEventSrcButton(event) {
    const path = eventPath(event);
    return find(path, (elem) => elem.tagName && elem.tagName.toLowerCase() === 'button');
}
/**
 * converts the string value from 24hr format to 12hr format
 * (helper function for convertTimescaleOnChange())
 * @param {string} value
 * @param {number} hour
 * @param {string} minute
 * @returns {string}
 */
function convert24to12(value, hour, minute) {
    if (hour < 12) {
        const newVal = hour === 0 ? `12:${minute}` : value;
        return `${newVal} AM`;
    }
    const newHour = hour === 12 ? hour : hour - 12;
    const paddedHour = newHour < 10 ? `0${newHour.toString()}` : newHour.toString();
    return `${paddedHour}:${minute} PM`;
}
/**
 * converts the string value from 12hr format to 24hr format
 * (helper function for convertTimescaleOnChange())
 * @param {string} value
 * @param {number} hour
 * @param {string} minute
 * @returns {string}
 */
function convert12to24(value, hour, minute) {
    const [suffix] = value.match(/([A|P]M)/) || ['AM'];
    if (suffix === 'AM') {
        const newTime = hour < 10 ? `0${hour.toString()}` : hour.toString();
        return hour === 12 ? `00:${minute}` : `${newTime}:${minute}`;
    }
    const newHour = hour === 12 ? 12 : hour + 12;
    return `${newHour.toString()}:${minute}`;
}
/**
 * Based on the time format given converts from either
 * 12hr format to 24hr format or the other way around.
 * @param {string} value
 * @param {boolean} isTwelveHourFormat
 * @returns {string}
 */
function convertTimescaleOnChange(value, isTwelveHourFormat) {
    if (value === '')
        return '';
    const currTime = value.match(/(\d{2})/g);
    if (!currTime || currTime.length < 2)
        return '';
    const hour = +currTime[0];
    const minute = currTime[1];
    if (isTwelveHourFormat) {
        // check if already 12-hour format
        if (value && value.match(/\s+[A|P]M/g))
            return value;
        // convert from 24-hour to 12-hour format
        return convert24to12(value, hour, minute);
    }
    // convert from 12-hour to 24-hour format
    return convert12to24(value, hour, minute);
}
/**
 * Get aria-label or aria-labelledby based on whether label text is visible or invisible
 */
function getLabelProps(label, assistiveText, labelId) {
    if (label) {
        return { [AriaAttr.LABELLEDBY]: labelId };
    }
    return { [AriaAttr.LABEL]: assistiveText };
}
/**
 * Check pointerType for keyboard "click" event (pointerType="")
 * 	This can be used to prevent BXF_MOUSE_FOCUS from being applied
 *  when a button click event is fired on Enter/Space.
 */
function isKeyboardClickEvent(event) {
    if (event) {
        switch (event.type) {
            case WindowEventMap.click:
                return !event.pointerType;
            case WindowEventMap.keydown:
                return true;
            case WindowEventMap.mousedown:
            case WindowEventMap.touchstart:
            default:
                return false;
        }
    }
    return false;
}
/**
 * Checks the updated value of keepMouseFocus and adds/remove BXF_MOUSE_FOCUS accordingly
 */
function toggleMouseFocus(elem, keepFocus) {
    if (elem && elem.classList) {
        if (keepFocus && elem.classList.contains(BXF_MOUSE_FOCUS)) {
            elem.classList.remove(BXF_MOUSE_FOCUS);
        }
        else if (!keepFocus && !elem.classList.contains(BXF_MOUSE_FOCUS)) {
            elem.classList.add(BXF_MOUSE_FOCUS);
        }
    }
}
/**
 * It can transform any special string case like kebab, snake, camel, or pascal into sentence case
 * @param originalString
 */
function transformToSentenceCase(originalString) {
    return isEmpty(originalString)
        ? ''
        : upperFirst(startCase(originalString).toLowerCase());
}
/**
 * Given a base className, and the value of keepMouseFocus, get new CSS with focus-visible styling.
 * @param baseClass base className
 * @param keepMouseFocus whether focus ring should be visible on mouse
 * @returns new CSS, trimmed
 */
function getKeyboardFocusCSS(baseClass, keepMouseFocus) {
    return `${baseClass} ${keepMouseFocus ? '' : BXF_FOCUS_KEYBOARD_ONLY}`.trim();
}
function getDataDifference(oldData, newData, comparisonFn = isEqual) {
    const newOld = differenceWith(newData, oldData, comparisonFn);
    return isEmpty(newOld)
        ? differenceWith(oldData, newData, comparisonFn)
        : newOld;
}
/**
 * Detects if the current browser is Safari.
 * @returns {boolean} True if the browser is Safari, false otherwise.
 */
function isSafariBrowser() {
    const ua = navigator.userAgent.toLowerCase();
    const isSafari = /^((?!chrome|android|crios|fxios|edg|opera|opr|samsung).)*safari/i.test(ua);
    return isSafari;
}

export { BXF_MOUSE_FOCUS as A, BXF_INLINE_MENU_TAGNAME as B, BXF_FOCUS_KEYBOARD_ONLY as C, CANVAS_ID as D, DEFAULT_Z_INDEX as E, DEFAULT_FONT as F, WINDOW_CONTROLS_OVERLAY_HEIGHT as G, HOVER_IN_DELAY as H, CHAT_COMPONENT_CONSOLE_WARNING as I, COPILOT_CANVAS_WRAPPER as J, AXF_HEADER_CONTAINER as K, GRID_POPUP as L, GRID_FLOATING_FILTER_INPUT as M, GRID_FLOATING_FILTER_BUTTON as N, AriaAttr as O, WindowEventMap as P, eventPath as Q, tabbable as R, WINDOW_CONTROLS_OVERLAY_ENABLED as W, addEventListenerOnElement as a, getElement as b, createElementWithClass as c, removeFromContainer as d, elementExists as e, formatTimestamp as f, getTabbableElements as g, hasSlot as h, isGridPopupInEscPath as i, getShadowRootElement as j, getSlottedElements as k, createSelector as l, sanitizeHTML as m, sanitizeSvg as n, getEventSrcButton as o, convertTimescaleOnChange as p, getLabelProps as q, removeEventListenerOnElement as r, setSVGHiContrastOverrides as s, isKeyboardClickEvent as t, toggleMouseFocus as u, transformToSentenceCase as v, getKeyboardFocusCSS as w, getDataDifference as x, isSafariBrowser as y, BXF_ASSISTIVE_TEXT_HIDDEN as z };
